import type { HikvisionDevice } from "@/store/oasis/hikvisionAccessStore";

type DoorDevice = Pick<HikvisionDevice, "door_no" | "readers">;

/** Puerta del DS-K2812 de la que cuelga una lectora: dos lectoras por puerta. */
export const doorOfReader = (readerNo: unknown): number | null => {
  const n = Number(readerNo);
  return Number.isInteger(n) && n > 0 ? Math.ceil(n / 2) : null;
};

/**
 * Las puertas que el mapa de lectoras reparte, en orden. Es la única cuenta de
 * "cuántas plumas tiene este documento" del front: la usan el alta manual y la
 * consola, para que no puedan discrepar. El backend tiene la suya
 * (doorsOfDevice en hikvisionAccessController.js), que además suma door_no.
 */
export const readerDoors = (device: DoorDevice): number[] =>
  [...new Set((device.readers || []).map((r) => doorOfReader(r.reader_no)).filter((d): d is number => d !== null))]
    .sort((a, b) => a - b);

/**
 * Qué puerta del controlador hay que abrir para un acceso manual.
 *
 * El alta manual abría siempre `device.door_no`, y eso sólo es correcto cuando
 * cada pluma tiene su propio documento. Acuapue no: es UN `hikvisionDevices`
 * bidireccional con `door_no: 1` sobre un DS-K2812 de dos puertas, con el mapa
 * `1:exit,3:entry` (medido en prod el 14-oct-2026). La lectora 1 cuelga de la
 * puerta 1 y la 3 de la puerta 2, así que con `door_no` fijo una ENTRADA
 * levantaba la pluma de SALIDA — pasó con las 2 entradas manuales del 11-jun.
 *
 * Sólo se deduce la puerta cuando el mapa abarca MÁS DE UNA puerta. Si no, manda
 * `door_no`: un documento por pluma creado por la API lleva sembrada la lectora
 * 1 aunque sea la puerta 2 (postHikvisionDevice), y Barranca/Carmelitas
 * (`1:entry,2:exit`) tienen las dos lectoras en la puerta 1. En los dos casos
 * `door_no` es el dato confiable y el mapa no dice nada de qué puerta abrir.
 *
 * `Number()` y no `Number.isInteger` a secas: la ingesta (directionFromReader)
 * coacciona igual, y un `reader_no: "3"` guardado como texto tiene que dar la
 * misma puerta acá que dirección allá.
 */
export function doorForDirection(device: DoorDevice, direction: "entry" | "exit"): number {
  const fallback = device.door_no || 1;
  if (readerDoors(device).length < 2) return fallback;
  const reader = (device.readers || []).find((r) => r.direction === direction);
  return (reader && doorOfReader(reader.reader_no)) || fallback;
}

export interface DoorLane {
  door: number;
  /** Direcciones de las lectoras de esa puerta: ["exit"], ["entry"], o las dos. */
  directions: ("entry" | "exit")[];
}

/**
 * Las plumas de un dispositivo de varias puertas, UNA POR PUERTA, para la
 * consola de control de acceso — que abría `door_no` y por lo tanto en Acuapue
 * sólo podía levantar la de salida.
 *
 * Por puerta y no por dirección: con dos lectoras de entrada en puertas
 * distintas, o una puerta con las dos direcciones, agrupar por dirección
 * repetía una puerta (dos botones para la misma pluma) y dejaba otra sin botón.
 * Así cada puerta del mapa tiene exactamente un par Abrir/Cerrar.
 *
 * Vacío cuando el mapa no abarca más de una puerta: ahí hay una sola pluma
 * (`door_no`) y la consola muestra el par de siempre.
 */
export function directionLanes(device: DoorDevice): DoorLane[] {
  const doors = readerDoors(device);
  if (doors.length < 2) return [];
  return doors.map((door) => {
    const dirs = new Set(
      (device.readers || []).filter((r) => doorOfReader(r.reader_no) === door).map((r) => r.direction),
    );
    return { door, directions: (["entry", "exit"] as const).filter((d) => dirs.has(d)) };
  });
}
